// src/pages/admin/Products.jsx - UPDATED TO MATCH VENDOR DESIGN EXACTLY
import React, { useState, useEffect } from 'react';
import { adminApi } from '../../api/adminApi';
import { useOptions } from '../../context/OptionsContext';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Stack,
  Avatar,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Rating,
  Divider,
  FormControl,
  InputLabel,
  Select,
  useMediaQuery,
  useTheme,
  Collapse,
  Badge as MuiBadge,
  Skeleton,
  Tooltip,
  Pagination,
  Snackbar,
  OutlinedInput,
  Checkbox,
  ListItemText,
  ListSubheader,
} from '@mui/material';
import {
  Add,
  Search,
  MoreVert,
  Edit,
  Delete,
  Visibility,
  Inventory,
  CheckCircle,
  Cancel,
  GridView,
  ViewList,
  Refresh,
  Download,
  Warning as WarningIcon,
  Store,
  Verified,
  Close,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';

const API_BASE_URL = process.env.REACT_APP_API_URL 
  ? process.env.REACT_APP_API_URL.replace('/api', '') 
  : 'https://api.goscitech.com';

// ITEM_HEIGHT for Select dropdown
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const AdminProducts = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const { categories, sizes, colors, badges, loading: optionsLoading } = useOptions();
  
  // State
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // View state
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterVendor, setFilterVendor] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 6 : 10);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    lowStock: 0,
  });

  // Dialogs
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [viewDetailsDialog, setViewDetailsDialog] = useState(false);
  const [expandedProduct, setExpandedProduct] = useState(null);
  
  // Product form state
  const [productFormData, setProductFormData] = useState({
    name: '',
    description: '',
    price: '',
    originalPrice: '',
    stock: '',
    categoryId: '',
    vendorId: '',
    sizes: [],
    colors: [],
    badge: '',
    images: [],
    thumbnail: '',
  });

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ============================================================
  // ✅ HELPER: Get image URL
  // ============================================================
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/placeholder-image.png';
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    return `${API_BASE_URL}${imagePath}`;
  };

  // ============================================================
  // ✅ HELPER: Calculate stats from products
  // ============================================================
  const calculateStatsFromProducts = (productList) => {
    const list = productList || products || [];
    setStats({
      total: list.length,
      active: list.filter(p => p.isActive).length,
      inactive: list.filter(p => !p.isActive).length,
      lowStock: list.filter(p => p.stock < 10 && p.stock > 0).length,
    });
  };

  // ============================================================
  // FETCH DATA
  // ============================================================
  const fetchProducts = async (currentPage = page) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: currentPage + 1,
        limit: rowsPerPage,
        search: searchTerm || undefined,
      };

      if (filterCategory !== 'all') {
        params.category = filterCategory;
      }
      if (filterVendor !== 'all') {
        params.vendor = filterVendor;
      }
      if (filterStatus === 'active') {
        params.isActive = true;
      } else if (filterStatus === 'inactive') {
        params.isActive = false;
      }

      const response = await adminApi.getAdminProducts(params);
      const data = response.data;
      
      setProducts(data.products || []);
      setTotalProducts(data.total || 0);
      setTotalPages(data.pages || 1);
      setPage(currentPage);

      calculateStatsFromProducts(data.products || []);
      
    } catch (error) {
      console.error('Error fetching products:', error);
      setError('Failed to load products. Please try again.');
      showSnackbar('Failed to load products', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await adminApi.getVendors({ limit: 9999 });
      const data = response.data;
      setVendors(data.vendors || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  useEffect(() => {
    fetchProducts(0);
    fetchVendors();
  }, []);

  useEffect(() => {
    if (page > 0) {
      fetchProducts(page);
    }
  }, [page]);

  // ============================================================
  // HANDLERS
  // ============================================================
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const handleFilterCategory = (category) => {
    setFilterCategory(category);
    setPage(0);
  };

  const handleFilterVendor = (vendor) => {
    setFilterVendor(vendor);
    setPage(0);
  };

  const handleFilterStatus = (status) => {
    setFilterStatus(status);
    setPage(0);
  };

  const handleSort = (e) => {
    setSortBy(e.target.value);
  };

  const handlePageChange = (event, value) => {
    fetchProducts(value - 1);
  };

  const handleAddProduct = () => {
    setEditingProduct(null);
    setProductFormData({
      name: '',
      description: '',
      price: '',
      originalPrice: '',
      stock: '',
      categoryId: '',
      vendorId: '',
      sizes: [],
      colors: [],
      badge: '',
      images: [],
      thumbnail: '',
    });
    setOpenDialog(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductFormData({
      name: product.name || '',
      description: product.description || '',
      price: product.price?.toString() || '',
      originalPrice: product.originalPrice?.toString() || '',
      stock: product.stock?.toString() || '',
      categoryId: product.CategoryId || product.categoryId || '',
      vendorId: product.VendorId || product.vendorId || '',
      sizes: product.sizes || [],
      colors: product.colors || [],
      badge: product.badge || '',
      images: product.images || [],
      thumbnail: product.thumbnail || '',
    });
    setOpenDialog(true);
    handleMenuClose();
  };

  const handleViewProduct = (product) => {
    setSelectedProduct(product);
    setViewDetailsDialog(true);
    handleMenuClose();
  };

  const handleDeleteProduct = async () => {
    if (!selectedProduct) return;
    try {
      await adminApi.deleteProduct(selectedProduct.id);
      showSnackbar(`Product "${selectedProduct.name}" deleted successfully`, 'success');
      setDeleteDialog(false);
      setSelectedProduct(null);
      fetchProducts(page);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to delete product', 'error');
    }
  };

  const handleToggleStatus = async (product) => {
    try {
      const newStatus = !product.isActive;
      await adminApi.updateProduct(product.id, { isActive: newStatus });
      showSnackbar(`Product "${product.name}" ${newStatus ? 'activated' : 'deactivated'} successfully`, 'success');
      fetchProducts(page);
      handleMenuClose();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to update product status', 'error');
    }
  };

  const handleMenuOpen = (event, product) => {
    setAnchorEl(event.currentTarget);
    setSelectedProduct(product);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExpandProduct = (productId) => {
    setExpandedProduct(expandedProduct === productId ? null : productId);
  };

  const handleFormChange = (field, value) => {
    setProductFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveProduct = async () => {
    try {
      const data = {
        name: productFormData.name,
        description: productFormData.description,
        price: parseFloat(productFormData.price),
        originalPrice: productFormData.originalPrice ? parseFloat(productFormData.originalPrice) : null,
        stock: parseInt(productFormData.stock),
        CategoryId: productFormData.categoryId,
        VendorId: productFormData.vendorId,
        sizes: productFormData.sizes,
        colors: productFormData.colors,
        badge: productFormData.badge,
        images: productFormData.images,
        thumbnail: productFormData.thumbnail,
      };

      if (editingProduct) {
        await adminApi.updateProduct(editingProduct.id, data);
        showSnackbar('Product updated successfully!', 'success');
      } else {
        await adminApi.createProduct(data);
        showSnackbar('Product created successfully!', 'success');
      }
      setOpenDialog(false);
      fetchProducts(page);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to save product', 'error');
    }
  };

  const handleRefresh = () => {
    fetchProducts(page);
    showSnackbar('Products refreshed', 'info');
  };

  // ============================================================
  // HELPERS
  // ============================================================
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStockColor = (stock) => {
    if (stock > 10) return 'success';
    if (stock > 0) return 'warning';
    return 'error';
  };

  const getVendorName = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    return vendor?.storeName || 'Unknown Vendor';
  };

  // ============================================================
  // RENDER: Mobile Product Card
  // ============================================================
  const renderMobileProductCard = (product) => {
    const isExpanded = expandedProduct === product.id;

    return (
      <Card key={product.id} sx={{ mb: 2, borderRadius: 2, position: 'relative' }}>
        <CardContent sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={2}>
              <Box sx={{ position: 'relative', flexShrink: 0 }}>
                <Avatar
                  variant="rounded"
                  src={getImageUrl(product.thumbnail || product.images?.[0])}
                  sx={{ width: 80, height: 80, borderRadius: 2 }}
                >
                  <Inventory />
                </Avatar>
                {product.badge && (
                  <Chip
                    label={product.badge}
                    size="small"
                    color="primary"
                    sx={{
                      position: 'absolute',
                      top: -4,
                      left: -4,
                      height: 18,
                      fontSize: '0.5rem',
                    }}
                  />
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight="bold" noWrap>
                  {product.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {product.Category?.name || 'No Category'}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                  <Store sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
                  {product.Vendor?.storeName || getVendorName(product.VendorId) || 'Unknown Vendor'}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    ${product.price?.toFixed(2) || '0.00'}
                  </Typography>
                  {product.originalPrice && (
                    <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                      ${product.originalPrice.toFixed(2)}
                    </Typography>
                  )}
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Rating value={product.rating || 0} size="small" readOnly />
                  <Typography variant="caption" color="text.secondary">
                    ({product.reviewsCount || 0})
                  </Typography>
                </Stack>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Chip
                icon={product.isActive ? <CheckCircle /> : <Cancel />}
                label={product.isActive ? 'Active' : 'Inactive'}
                size="small"
                color={product.isActive ? 'success' : 'error'}
                sx={{ height: 24 }}
              />
              <Chip
                label={`Stock: ${product.stock || 0}`}
                size="small"
                color={getStockColor(product.stock || 0)}
                variant="outlined"
                sx={{ height: 24 }}
              />
              {product.stock < 10 && product.stock > 0 && (
                <Chip
                  icon={<WarningIcon />}
                  label="Low Stock"
                  size="small"
                  color="warning"
                  sx={{ height: 24 }}
                />
              )}
            </Stack>

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                size="small"
                variant="outlined"
                startIcon={<Visibility />}
                onClick={() => {
                  setSelectedProduct(product);
                  setViewDetailsDialog(true);
                }}
                sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.7rem' }}
              >
                View
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => handleEditProduct(product)}
                sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.7rem' }}
              >
                Edit
              </Button>
              <IconButton size="small" onClick={() => handleExpandProduct(product.id)}>
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Stack>

            <Collapse in={isExpanded}>
              <Divider sx={{ my: 1.5 }} />
              <Stack spacing={1.5}>
                {product.sizes && product.sizes.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      Sizes
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                      {product.sizes.map((size) => (
                        <Chip key={size} label={size} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </Box>
                )}
                
                {product.colors && product.colors.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      Colors
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                      {product.colors.map((color) => (
                        <Chip
                          key={color}
                          label={color}
                          size="small"
                          sx={{ bgcolor: color.toLowerCase(), color: '#fff' }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                {product.description && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      Description
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {product.description.length > 100 
                        ? `${product.description.substring(0, 100)}...` 
                        : product.description}
                    </Typography>
                  </Box>
                )}

                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    Vendor
                  </Typography>
                  <Typography variant="body2">
                    {product.Vendor?.storeName || getVendorName(product.VendorId) || 'Unknown'}
                  </Typography>
                </Box>

                <Button
                  size="small"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => {
                    setSelectedProduct(product);
                    setDeleteDialog(true);
                  }}
                  sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                >
                  Delete Product
                </Button>
              </Stack>
            </Collapse>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  // ============================================================
  // RENDER: Grid View
  // ============================================================
  const renderGridView = () => (
    <Grid container spacing={isMobile ? 1.5 : 3}>
      {products.map((product) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
          <Card sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            borderRadius: 2,
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: theme.shadows[8],
            },
          }}>
            <Box sx={{ position: 'relative', pt: '100%', bgcolor: 'grey.100' }}>
              <CardMedia
                component="img"
                image={getImageUrl(product.thumbnail || product.images?.[0])}
                alt={product.name}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              {product.badge && (
                <Chip
                  label={product.badge}
                  size="small"
                  color="primary"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    fontWeight: 'bold',
                    height: 24,
                    fontSize: '0.65rem',
                  }}
                />
              )}
              {product.stock < 10 && product.stock > 0 && (
                <Chip
                  icon={<WarningIcon sx={{ fontSize: 14 }} />}
                  label="Low Stock"
                  size="small"
                  color="warning"
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    height: 24,
                    fontSize: '0.65rem',
                  }}
                />
              )}
              {product.stock === 0 && (
                <Chip
                  label="Out of Stock"
                  size="small"
                  color="error"
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    height: 24,
                    fontSize: '0.65rem',
                  }}
                />
              )}
              {!product.isActive && (
                <Chip
                  label="Inactive"
                  size="small"
                  color="default"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    height: 24,
                    fontSize: '0.65rem',
                  }}
                />
              )}
            </Box>

            <CardContent sx={{ flexGrow: 1, p: 2 }}>
              <Typography variant="subtitle1" fontWeight="600" noWrap>
                {product.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {product.Category?.name || 'No Category'}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" noWrap>
                <Store sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
                {product.Vendor?.storeName || getVendorName(product.VendorId) || 'Unknown Vendor'}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                <Typography variant="h6" color="primary" fontWeight="bold">
                  ${product.price?.toFixed(2) || '0.00'}
                </Typography>
                {product.originalPrice && (
                  <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                    ${product.originalPrice.toFixed(2)}
                  </Typography>
                )}
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                <Rating value={product.rating || 0} size="small" readOnly />
                <Typography variant="caption" color="text.secondary">
                  ({product.reviewsCount || 0})
                </Typography>
              </Stack>
              <Chip
                icon={product.isActive ? <CheckCircle /> : <Cancel />}
                label={product.isActive ? 'Active' : 'Inactive'}
                size="small"
                color={product.isActive ? 'success' : 'error'}
                sx={{ mt: 1, height: 24, fontSize: '0.65rem' }}
              />
              <Chip
                label={`Stock: ${product.stock || 0}`}
                size="small"
                color={getStockColor(product.stock || 0)}
                variant="outlined"
                sx={{ mt: 0.5, height: 24, fontSize: '0.65rem' }}
              />
            </CardContent>

            <CardActions sx={{ justifyContent: 'flex-end', p: 2, pt: 0 }}>
              <Button
                size="small"
                startIcon={<Edit />}
                onClick={() => handleEditProduct(product)}
                sx={{ textTransform: 'none' }}
              >
                Edit
              </Button>
              <IconButton
                size="small"
                onClick={(e) => handleMenuOpen(e, product)}
              >
                <MoreVert />
              </IconButton>
            </CardActions>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  // ============================================================
  // RENDER: List View
  // ============================================================
  const renderListView = () => (
    <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
      <TableContainer>
        <Table>
          <TableHead sx={{ bgcolor: 'grey.50' }}>
            <TableRow>
              <TableCell><Typography variant="subtitle2" fontWeight="bold">Product</Typography></TableCell>
              <TableCell><Typography variant="subtitle2" fontWeight="bold">Vendor</Typography></TableCell>
              <TableCell><Typography variant="subtitle2" fontWeight="bold">Category</Typography></TableCell>
              <TableCell align="right"><Typography variant="subtitle2" fontWeight="bold">Price</Typography></TableCell>
              <TableCell align="right"><Typography variant="subtitle2" fontWeight="bold">Stock</Typography></TableCell>
              <TableCell><Typography variant="subtitle2" fontWeight="bold">Status</Typography></TableCell>
              <TableCell align="right"><Typography variant="subtitle2" fontWeight="bold">Actions</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((product) => (
              <TableRow key={product.id} hover>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar
                      variant="rounded"
                      src={getImageUrl(product.thumbnail || product.images?.[0])}
                      sx={{ width: 48, height: 48 }}
                    >
                      <Inventory />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight="600">
                        {product.name}
                      </Typography>
                      {product.badge && (
                        <Chip
                          label={product.badge}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ mt: 0.5, height: 20, fontSize: '0.6rem' }}
                        />
                      )}
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {product.Vendor?.storeName || getVendorName(product.VendorId) || 'Unknown'}
                  </Typography>
                </TableCell>
                <TableCell>{product.Category?.name || 'N/A'}</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="600" color="primary">
                    ${product.price?.toFixed(2) || '0.00'}
                  </Typography>
                  {product.originalPrice && (
                    <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                      ${product.originalPrice.toFixed(2)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Chip
                    label={product.stock || 0}
                    size="small"
                    color={getStockColor(product.stock || 0)}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    icon={product.isActive ? <CheckCircle /> : <Cancel />}
                    label={product.isActive ? 'Active' : 'Inactive'}
                    size="small"
                    color={product.isActive ? 'success' : 'error'}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, product)}
                  >
                    <MoreVert />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  // ============================================================
  // MAIN RENDER
  // ============================================================
  if (loading && products.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: { xs: 8, sm: 10 } }}>
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          alignItems={{ xs: 'flex-start', sm: 'center' }} 
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
              Products
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage all platform products ({totalProducts} total)
            </Typography>
          </Box>
          <Stack 
            direction="row" 
            spacing={1} 
            width={{ xs: '100%', sm: 'auto' }}
            flexWrap="wrap"
          >
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              size={isMobile ? 'small' : 'medium'}
              fullWidth={isMobile}
              sx={{ flex: isMobile ? 1 : 'none' }}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              size={isMobile ? 'small' : 'medium'}
              fullWidth={isMobile}
              sx={{ flex: isMobile ? 1 : 'none' }}
            >
              Export
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddProduct}
              size={isMobile ? 'small' : 'medium'}
              fullWidth={isMobile}
              sx={{ flex: isMobile ? 1 : 'none' }}
            >
              Add Product
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={isMobile ? 1.5 : 3} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <Inventory color="primary" sx={{ fontSize: { xs: 28, sm: 40 }, mb: 0.5 }} />
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
                {stats.total}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Products
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <CheckCircle color="success" sx={{ fontSize: { xs: 28, sm: 40 }, mb: 0.5 }} />
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="success.main">
                {stats.active}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Active
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <Cancel color="error" sx={{ fontSize: { xs: 28, sm: 40 }, mb: 0.5 }} />
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="error.main">
                {stats.inactive}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Inactive
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', p: { xs: 1.5, sm: 2 } }}>
              <WarningIcon sx={{ fontSize: { xs: 28, sm: 40 }, color: 'warning.main' }} />
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="warning.main">
                {stats.lowStock}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Low Stock
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Toolbar */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3, borderRadius: 2 }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={isMobile ? 1 : 2} 
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <TextField
            placeholder="Search products..."
            value={searchTerm}
            onChange={handleSearch}
            size="small"
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          <Stack 
            direction="row" 
            spacing={1} 
            flexWrap="wrap"
            sx={{ gap: { xs: 0.5, sm: 1 } }}
          >
            <FormControl size="small" sx={{ minWidth: { xs: 100, sm: 120 } }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={filterCategory}
                onChange={(e) => handleFilterCategory(e.target.value)}
                label="Category"
              >
                <MenuItem value="all">All Categories</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { xs: 100, sm: 120 } }}>
              <InputLabel>Vendor</InputLabel>
              <Select
                value={filterVendor}
                onChange={(e) => handleFilterVendor(e.target.value)}
                label="Vendor"
              >
                <MenuItem value="all">All Vendors</MenuItem>
                {vendors.map((vendor) => (
                  <MenuItem key={vendor.id} value={vendor.id}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Store sx={{ fontSize: 16 }} />
                      <span>{vendor.storeName || `Vendor ${vendor.id}`}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { xs: 80, sm: 100 } }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => handleFilterStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { xs: 100, sm: 120 } }}>
              <InputLabel>Sort</InputLabel>
              <Select
                value={sortBy}
                onChange={handleSort}
                label="Sort"
              >
                <MenuItem value="newest">Newest</MenuItem>
                <MenuItem value="oldest">Oldest</MenuItem>
                <MenuItem value="price_asc">Price: Low to High</MenuItem>
                <MenuItem value="price_desc">Price: High to Low</MenuItem>
                <MenuItem value="name_asc">Name: A to Z</MenuItem>
                <MenuItem value="name_desc">Name: Z to A</MenuItem>
              </Select>
            </FormControl>

            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title="Grid View">
                <IconButton
                  onClick={() => setViewMode('grid')}
                  color={viewMode === 'grid' ? 'primary' : 'default'}
                  size={isMobile ? 'small' : 'medium'}
                >
                  <GridView />
                </IconButton>
              </Tooltip>
              <Tooltip title="List View">
                <IconButton
                  onClick={() => setViewMode('list')}
                  color={viewMode === 'list' ? 'primary' : 'default'}
                  size={isMobile ? 'small' : 'medium'}
                >
                  <ViewList />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      {/* Content */}
      {products.length === 0 ? (
        <Paper sx={{ p: { xs: 4, sm: 6 }, borderRadius: 3, textAlign: 'center' }}>
          <Inventory sx={{ fontSize: { xs: 48, sm: 64 }, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No products found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {searchTerm || filterCategory !== 'all' || filterStatus !== 'all' || filterVendor !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Start adding products to the platform'}
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddProduct}
          >
            Add Product
          </Button>
        </Paper>
      ) : (
        <>
          {isMobile ? (
            <Box>
              {products.map((product) => renderMobileProductCard(product))}
            </Box>
          ) : (
            viewMode === 'grid' ? renderGridView() : renderListView()
          )}

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page + 1}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? 'small' : 'medium'}
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { borderRadius: 2, minWidth: 180 }
        }}
      >
        <MenuItem onClick={() => handleViewProduct(selectedProduct)}>
          <Visibility fontSize="small" sx={{ mr: 1.5 }} /> View Details
        </MenuItem>
        <MenuItem onClick={() => handleEditProduct(selectedProduct)}>
          <Edit fontSize="small" sx={{ mr: 1.5 }} /> Edit
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleToggleStatus(selectedProduct)}>
          {selectedProduct?.isActive ? (
            <>
              <Cancel fontSize="small" sx={{ mr: 1.5, color: 'error.main' }} /> Deactivate
            </>
          ) : (
            <>
              <CheckCircle fontSize="small" sx={{ mr: 1.5, color: 'success.main' }} /> Activate
            </>
          )}
        </MenuItem>
        <MenuItem onClick={() => setDeleteDialog(true)} sx={{ color: 'error.main' }}>
          <Delete fontSize="small" sx={{ mr: 1.5 }} /> Delete
        </MenuItem>
      </Menu>

      {/* ============================================================ */}
      {/* ADD/EDIT PRODUCT DIALOG - MATCHES VENDOR DESIGN */}
      {/* ============================================================ */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, m: { xs: 1, sm: 2 } }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">
            {editingProduct ? 'Edit Product' : 'Add New Product'}
          </Typography>
          <IconButton onClick={() => setOpenDialog(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Vendor Dropdown */}
            <FormControl fullWidth required>
              <InputLabel>Vendor *</InputLabel>
              <Select
                value={productFormData.vendorId}
                onChange={(e) => handleFormChange('vendorId', e.target.value)}
                label="Vendor *"
              >
                {vendors.map((vendor) => (
                  <MenuItem key={vendor.id} value={vendor.id}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Store sx={{ fontSize: 18 }} />
                      <span>{vendor.storeName || `Vendor ${vendor.id}`}</span>
                      {vendor.isVerified && (
                        <Verified sx={{ fontSize: 14, color: 'success.main' }} />
                      )}
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Product Name *"
              value={productFormData.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              required
            />

            <TextField
              fullWidth
              label="Description"
              value={productFormData.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              multiline
              rows={3}
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Price *"
                  type="number"
                  value={productFormData.price}
                  onChange={(e) => handleFormChange('price', e.target.value)}
                  required
                  InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Original Price"
                  type="number"
                  value={productFormData.originalPrice}
                  onChange={(e) => handleFormChange('originalPrice', e.target.value)}
                  InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Stock *"
                  type="number"
                  value={productFormData.stock}
                  onChange={(e) => handleFormChange('stock', e.target.value)}
                  required
                  InputProps={{ inputProps: { min: 0 } }}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Category *</InputLabel>
                  <Select
                    value={productFormData.categoryId}
                    onChange={(e) => handleFormChange('categoryId', e.target.value)}
                    label="Category *"
                  >
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Sizes - Multiselect with Checkboxes - Matches Vendor Design */}
            <FormControl fullWidth>
              <InputLabel>Sizes</InputLabel>
              <Select
                multiple
                value={productFormData.sizes}
                onChange={(e) => handleFormChange('sizes', e.target.value)}
                input={<OutlinedInput label="Sizes" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {sizes.map((size) => {
                  const sizeName = size.name || size;
                  return (
                    <MenuItem key={size.id || sizeName} value={sizeName}>
                      <Checkbox checked={productFormData.sizes.indexOf(sizeName) > -1} />
                      <ListItemText primary={sizeName} />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            {/* Colors - Multiselect with Checkboxes - Matches Vendor Design */}
            <FormControl fullWidth>
              <InputLabel>Colors</InputLabel>
              <Select
                multiple
                value={productFormData.colors}
                onChange={(e) => handleFormChange('colors', e.target.value)}
                input={<OutlinedInput label="Colors" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={value}
                        size="small"
                        sx={{ bgcolor: value.toLowerCase(), color: '#fff' }}
                      />
                    ))}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {colors.map((color) => {
                  const colorName = color.name || color;
                  return (
                    <MenuItem key={color.id || colorName} value={colorName}>
                      <Checkbox checked={productFormData.colors.indexOf(colorName) > -1} />
                      <ListItemText primary={colorName} />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            {/* Badge Dropdown */}
            <FormControl fullWidth>
              <InputLabel>Badge (Optional)</InputLabel>
              <Select
                value={productFormData.badge}
                onChange={(e) => handleFormChange('badge', e.target.value)}
                label="Badge (Optional)"
              >
                <MenuItem value="">None</MenuItem>
                {badges.map((badge) => {
                  const badgeName = badge.name || badge;
                  return (
                    <MenuItem key={badge.id || badgeName} value={badgeName}>
                      {badgeName}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Thumbnail URL"
              value={productFormData.thumbnail}
              onChange={(e) => handleFormChange('thumbnail', e.target.value)}
              placeholder="https://example.com/image.jpg"
            />

            <TextField
              fullWidth
              label="Images (comma separated URLs)"
              value={productFormData.images?.join(', ')}
              onChange={(e) => handleFormChange('images', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
              placeholder="https://example.com/image1.jpg, https://example.com/image2.jpg"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={() => setOpenDialog(false)} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveProduct}
            fullWidth={isMobile}
            disabled={!productFormData.name || !productFormData.price || !productFormData.vendorId}
          >
            {editingProduct ? 'Update Product' : 'Create Product'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog
        open={viewDetailsDialog}
        onClose={() => setViewDetailsDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, m: { xs: 1, sm: 2 } }
        }}
      >
        <DialogTitle>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            alignItems={{ xs: 'flex-start', sm: 'center' }} 
            justifyContent="space-between"
            spacing={1}
          >
            <Typography variant="h6" fontWeight="bold">
              {selectedProduct?.name}
            </Typography>
            <Chip
              icon={selectedProduct?.isActive ? <CheckCircle /> : <Cancel />}
              label={selectedProduct?.isActive ? 'Active' : 'Inactive'}
              color={selectedProduct?.isActive ? 'success' : 'error'}
            />
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {selectedProduct && (
            <Stack spacing={3}>
              {selectedProduct.images && selectedProduct.images.length > 0 && (
                <Stack direction="row" spacing={2} sx={{ overflow: 'auto', pb: 1 }}>
                  {selectedProduct.images.map((image, index) => (
                    <Avatar
                      key={index}
                      variant="rounded"
                      src={getImageUrl(image)}
                      sx={{ width: 80, height: 80, borderRadius: 2 }}
                    />
                  ))}
                </Stack>
              )}

              <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    Category
                  </Typography>
                  <Typography variant="body2">{selectedProduct.Category?.name || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    Vendor
                  </Typography>
                  <Typography variant="body2">
                    {selectedProduct.Vendor?.storeName || getVendorName(selectedProduct.VendorId) || 'Unknown'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    Badge
                  </Typography>
                  <Typography variant="body2">{selectedProduct.badge || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    Price
                  </Typography>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    ${selectedProduct.price?.toFixed(2) || '0.00'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    Original Price
                  </Typography>
                  <Typography variant="body2">
                    {selectedProduct.originalPrice ? `$${selectedProduct.originalPrice.toFixed(2)}` : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    Stock
                  </Typography>
                  <Typography variant="body2" fontWeight="600">
                    {selectedProduct.stock || 0}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    Rating
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Rating value={selectedProduct.rating || 0} readOnly />
                    <Typography variant="body2">
                      ({selectedProduct.reviewsCount || 0} reviews)
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>

              <Divider />

              <Grid container spacing={2}>
                {selectedProduct.sizes && selectedProduct.sizes.length > 0 && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      Sizes
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap' }}>
                      {selectedProduct.sizes.map((size) => (
                        <Chip key={size} label={size} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </Grid>
                )}
                {selectedProduct.colors && selectedProduct.colors.length > 0 && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      Colors
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap' }}>
                      {selectedProduct.colors.map((color) => (
                        <Chip
                          key={color}
                          label={color}
                          size="small"
                          sx={{ bgcolor: color.toLowerCase(), color: '#fff' }}
                        />
                      ))}
                    </Stack>
                  </Grid>
                )}
              </Grid>

              <Divider />

              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                  Description
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {selectedProduct.description || 'No description provided'}
                </Typography>
              </Box>

              <Typography variant="caption" color="text.secondary">
                Created: {formatDate(selectedProduct.createdAt)}
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={() => setViewDetailsDialog(false)} fullWidth={isMobile}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => {
              setViewDetailsDialog(false);
              handleEditProduct(selectedProduct);
            }}
            fullWidth={isMobile}
          >
            Edit Product
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        maxWidth="xs"
        PaperProps={{
          sx: { borderRadius: 3, m: { xs: 1, sm: 2 } }
        }}
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          <Delete sx={{ mr: 1, verticalAlign: 'middle' }} /> Delete Product
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>"{selectedProduct?.name}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={() => setDeleteDialog(false)} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteProduct}
            color="error"
            variant="contained"
            fullWidth={isMobile}
            startIcon={<Delete />}
          >
            Delete Product
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminProducts;